"use client";
import {
  InvestorIllustration,
  NoSubscriptions,
  PropertyIllustration,
} from "@/components/bnp-illustrations";
import { Button, DropdownMenu, Hero, Nav } from "@/components/bnp-ui";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import { RealtorProfile, LoadMore, HowToListVideo } from "@/components/shared";
import SearchFilter from "@/components/shared/SearchFilter";
import DeveloperService from "@/services/developer";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { safeMoment } from "@/utils/safeMoment";
import parseHttpError from "@/utils/parseHttpError";

import Image from "next/image";
import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import Link from "next/link";
import useAuthToken from "@/hooks/useAuthToken";
import FooterAd from "@/components/shared/ads/FooterAd";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage, AdPlacement } from "@/types/advertising";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import PropertyCtaBanner from "@/components/shared/PropertyCtaBanner";
import { ContentSidebar } from "@/components/layouts/Others/ContentSidebar";

interface FormData {
  search?: string;
  location?: string;
}

const Developers = () => {
  const { user, token } = useAuthToken();
  const [allLoadedDevelopers, setAllLoadedDevelopers] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const router = useRouter();
  const { search, location } = router.query;
  const pageSize = 12;

  // NOTE: The backend only serves ads on the homepage for now. We fetch the
  // homepage ad set here so the placements render while testing; swap back to
  // AdPage.DEVELOPERS once real page ads are available.
  const { inFeedAds, footerAds, headerAds, popupAds, sidebarAds } = usePageAds(
    AdPage.HOME,
  );

  const defaultValues: FormData = {
    search: typeof search === "string" ? search : "",
    location: typeof location === "string" ? location : "",
  };

  const validationSchema = yup.object().shape({
    search: yup.string().optional(),
    location: yup.string().optional(),
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (search) setValue("search", search as string);
    if (location) setValue("location", location as string);
  }, [search, location, setValue]);

  const onSubmit = (data: FormData) => {
    const searchParams = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value) {
        searchParams.append(key, value.toString());
      }
    });
    router.push({
      pathname: "/developers",
      query: searchParams.toString(),
    });
  };

  const getDevelopersRequest = async () => {
    try {
      const queryParams: any = {
        pageNumber: currentPage,
        pageSize,
      };

      if (search) queryParams.search = search;
      if (location) queryParams.location = location;

      const response = await DeveloperService.getAllDevelopers(
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
      );
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: developerData,
    isLoading: developersIsLoading,
    isError: developersIsError,
    refetch: developersRefetch,
    isSuccess: developersIsSuccess,
  } = useQuery({
    queryKey: ["developers", currentPage, pageSize, search, location],
    queryFn: getDevelopersRequest,
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (developerData) {
      const data = developerData?.data || [];
      if (currentPage === 1) {
        setAllLoadedDevelopers(data);
      } else {
        setAllLoadedDevelopers((prev: any[]) => [...prev, ...data]);
      }
      setTotalCount(developerData?.metadata?.totalCount || 0);
    }
  }, [developerData, currentPage]);

  const developers = allLoadedDevelopers;
  const hasMore = developers.length < totalCount;
  const remaining = totalCount - developers.length;

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
  };

  useEffect(() => {
    if (currentPage > 1 && developerData) {
      setIsLoadingMore(false);
    }
  }, [currentPage, developerData]);

  const handleViewDeveloper = (developerId: string) => {
    router.push(`/developers/${developerId}`);
  };

  const developersStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: developers
      .slice(0, 10)
      .map((developer: any, index: number) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "LocalBusiness",
          name: developer?.full_name || "Developer",
          url: `https://www.findnigeriaproperties.com/developers/${developer?.slug || developer?.id}`,
          address: {
            "@type": "PostalAddress",
            addressLocality: developer?.city || "",
            addressRegion: developer?.state || "",
            addressCountry: "NG",
          },
        },
      })),
  };

  return (
    <>
      {developers.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(developersStructuredData),
          }}
        />
      )}
      <HomeWrapper>
        {headerAds[0] && (
          <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
            <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
          </div>
        )}

        <ContentSidebar
          sidebarContent={
            sidebarAds?.[0] && (
              <div className="w-full mx-auto px-4 pt-4 pb-2">
                <AdRenderer
                  ad={sidebarAds[0]}
                  placement={AdPlacement.SIDEBAR}
                />
              </div>
            )
          }
          topOffset={200}
        >
          <div className={containerWidth}>
            <div className="relative w-full max-w-[1104px] mx-auto px-4 sm:px-6 lg:px-8">
              {/* Search Section */}
              <SearchFilter
                control={control}
                setValue={setValue}
                onSubmit={handleSubmit(onSubmit)}
              />

              {/* Developers Grid */}
              <section className="py-8 md:py-12">
                <div className="mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    Browse Developers
                  </h2>
                  {/* <p className="text-gray-500 text-sm md:text-base">
                {developers.length > 0
                  ? `Showing ${developers.length} trusted developer${developers.length !== 1 ? 's' : ''}`
                  : 'No developers found matching your criteria'}
              </p> */}
                </div>

                {developersIsLoading ? (
                  <div className="flex flex-col justify-center items-center w-full py-20">
                    <div className="w-fit h-fit">
                      <Image
                        //@ts-ignore
                        src={"/assets/images/property-default.svg"}
                        width={200}
                        height={200}
                        alt={"loading"}
                        className="w-[200px] h-[200px] object-cover animate-pulse"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {developers.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {developers.map((realtor: any, index: any) => (
                          <React.Fragment key={index}>
                            <RealtorProfile
                              full_name={
                                realtor?.business_name ||
                                [realtor?.first_name, realtor?.last_name]
                                  .filter(Boolean)
                                  .join(" ") ||
                                ""
                              }
                              joinedYears={
                                safeMoment(realtor?.join_date)?.fromNow() ?? "-"
                              }
                              listedProperties={
                                realtor?.listed_properties_count
                              }
                              location={
                                realtor?.business_address
                                  ? realtor.business_address
                                  : realtor?.address
                                    ? realtor.address
                                    : [realtor?.state, realtor?.city]
                                        .filter(Boolean)
                                        .join(", ")
                              }
                              whatsapp={realtor?.phone_number}
                              realtorId={realtor?.id}
                              onViewProperties={() =>
                                handleViewDeveloper(realtor.id)
                              }
                              profile_image_url={realtor?.profile_image_url}
                            />
                            {/* In-feed ad after the first two rows */}
                            {index === 5 && inFeedAds[0] && (
                              <div className="col-span-full">
                                <AdRenderer
                                  ad={inFeedAds[0]}
                                  placement={AdPlacement.IN_FEED}
                                />
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col justify-center items-center w-full py-20">
                        <NoSubscriptions />
                        <p className="text-grey-7 lg:w-[408px] text-center mt-4 text-lg">
                          No Developers found
                        </p>
                      </div>
                    )}
                  </>
                )}
              </section>

              <LoadMore
                hasMore={hasMore}
                isLoading={developersIsLoading || isLoadingMore}
                onLoadMore={handleLoadMore}
                loadedCount={developers.length}
                totalCount={totalCount}
                pageSize={pageSize}
                className="w-full"
              />

              {/* ===== HOW TO LIST VIDEO ===== */}
              <HowToListVideo />

              {/* ===== CTA BANNER ===== */}
              <PropertyCtaBanner />
            </div>
          </div>
        </ContentSidebar>
        {popupAds[0] && (
          <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
        )}

        {footerAds[0] && (
          <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
            <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
          </div>
        )}
      </HomeWrapper>
    </>
  );
};

export default Developers;
